import 'styles/pages/premier.scss';
import '~scripts/main';

// Packages
import Vue from 'vue';
import eases from 'eases';

// Core
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { isInViewport, isSafari } from '~scripts/core/helpers';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';
import ClientsLogotypes from '~scripts/blocks/vue/ClientsLogotypes.vue';
import initRequestForm from '~scripts/blocks/request-form';

// Components
import '~scripts/components/modals';

// Media
BREAKPOINTS.mdMax = '1160px';
BREAKPOINTS.lgMin = '1161px';
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);
const mediaMd = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin}) and (max-width: ${BREAKPOINTS.mdMax})`);
const mediaLg = window.matchMedia(`(min-width: ${BREAKPOINTS.lgMin})`);

// Variables
const html = document.documentElement;
const body = document.body;

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// BANNER
//
// Photo
const bannerEl = document.getElementById('banner');
let stopBannerPhotoAnimation = null;

const handleBannerPhotoAnimation = () => {
  const bannerPhoto = document.getElementById('banner-photo');

  if (mediaSm.matches) {
    if (stopBannerPhotoAnimation && typeof stopBannerPhotoAnimation === 'function') {
      stopBannerPhotoAnimation();
    }
  } else {
    stopBannerPhotoAnimation = scrollAnimationProgress(bannerPhoto, {
      getStart() {
        return 0;
      },
      getEnd(el, start) {
        const bannerHeight = bannerEl.offsetHeight;
        return start + bannerHeight;
      },
    });
  }
};

handleBannerPhotoAnimation();
mediaSm.addEventListener('change', handleBannerPhotoAnimation);

//
// FEATURES
//
// Items
let stopFeaturesItemsAnimation = [];

const handleFeaturesItemsAnimation = () => {
  const featuresItems = document.querySelectorAll('#features-items > li');

  if (mediaSm.matches) {
    stopFeaturesItemsAnimation.forEach((stop) => stop());
  } else {
    stopFeaturesItemsAnimation = Array.from(featuresItems, (featureItem) => {
      return scrollAnimationProgress(featureItem, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          const height = window.innerHeight;
          return rect.top + window.scrollY - (height - height / 4);
        },
        getEnd(el, start) {
          return start + window.innerHeight / 2;
        },
      });
    });
  }
};

handleFeaturesItemsAnimation();
mediaSm.addEventListener('change', handleFeaturesItemsAnimation);

//
// CLIENTS
//
new Vue({
  el: '#VClientsLogotypes',
  name: 'VClientsLogotypes',
  components: {
    ClientsLogotypes,
  },
});

// Animation
const handleClientsAnimationsScroll = () => {
  const windowHeight = window.innerHeight;
  const clientsTitle = document.getElementById('clients-title');
  const clientsTitleRect = clientsTitle.getBoundingClientRect();
  const clientsLogos = document.getElementById('VClientsLogotypes');
  const clientsLogosRect = clientsLogos.getBoundingClientRect();

  if (clientsTitleRect.top - windowHeight <= 0 && clientsTitle.classList.contains('is-animatable')) {
    clientsTitle.classList.add('is-animated');
  }

  if (clientsLogosRect.top - windowHeight <= 0 && clientsLogos.classList.contains('is-animatable')) {
    clientsLogos.classList.add('is-animated');
  }
};

const handleClientsAnimationsInit = () => {
  if (mediaSm.matches) {
    window.removeEventListener('scroll', handleClientsAnimationsScroll);
  } else {
    window.addEventListener('scroll', handleClientsAnimationsScroll);
  }
};

handleClientsAnimationsInit();
mediaSm.addEventListener('change', handleClientsAnimationsInit);

//
// SECTIONS
//
const sectionsTitleOuter = document.getElementById('sections-title-outer');
const sectionsTitleInner = document.getElementById('sections-title-inner');

scrollAnimationProgress(sectionsTitleInner, {
  getStart(el) {
    const rect = el.getBoundingClientRect();
    return rect.top + window.scrollY - window.innerHeight;
  },
  getEnd(el, start) {
    const height = el.offsetHeight;
    return start + (window.innerHeight / 2 + height / 3);
  },
});

scrollAnimationProgress(sectionsTitleOuter, {
  getStart(el) {
    const rect = el.getBoundingClientRect();
    const height = el.offsetHeight;
    return rect.top + window.scrollY - (window.innerHeight / 2 - height / 1.5);
  },
  getEnd(el, start) {
    const height = el.offsetHeight;
    return start + (window.innerHeight / 2 + height / 1.5);
  },
});

//
// SECTION
//
// TITLE
const handleSectionTitleAnimationScroll = () => {
  const windowHeight = window.innerHeight;
  const titles = document.querySelectorAll('.js-section-title.is-animatable');

  titles.forEach((title) => {
    const elementRect = title.getBoundingClientRect();

    if (elementRect.top - windowHeight < -150) {
      title.classList.add('is-animated');
    }
  });
};

const handleSectionAnimationsInit = () => {
  if (mediaSm.matches) {
    window.removeEventListener('scroll', handleSectionTitleAnimationScroll);
  } else {
    window.addEventListener('scroll', handleSectionTitleAnimationScroll);
  }
};

handleSectionAnimationsInit();
mediaSm.addEventListener('change', handleSectionAnimationsInit);

// PHOTO
// Scaling
let stopSectionPhotoScalingAnimation = [];

const handleSectionPhotoScalingAnimation = () => {
  const sectionPhotosElements = document.querySelectorAll(
    '.js-section-photo.is-animatable, .js-section-photo.is-animatable > picture > img'
  );

  if (mediaSm.matches) {
    stopSectionPhotoScalingAnimation.forEach((stop) => stop());
  } else {
    stopSectionPhotoScalingAnimation = Array.from(sectionPhotosElements, (sectionPhotoElement) => {
      return scrollAnimationProgress(
        sectionPhotoElement,
        {
          getStart(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + window.scrollY - window.innerHeight;
          },
          getEnd(el, start) {
            const height = el.offsetHeight;
            return start + height / 3;
          },
          lerpValue: 0.2,
        },
        eases.linear
      );
    });
  }
};

handleSectionPhotoScalingAnimation();
mediaSm.addEventListener('change', handleSectionPhotoScalingAnimation);

// Parallax
let stopSectionPhotoParallaxAnimation = [];

const handleSectionPhotoParallaxAnimation = () => {
  const sectionPhotosPictures = document.querySelectorAll('.js-section-photo.is-animatable > picture');

  if (mediaSm.matches) {
    stopSectionPhotoParallaxAnimation.forEach((stop) => stop());
  } else {
    stopSectionPhotoParallaxAnimation = Array.from(sectionPhotosPictures, (sectionPhotoPicture) => {
      return scrollAnimationProgress(sectionPhotoPicture, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + window.innerHeight + height;
        },
      });
    });
  }
};

handleSectionPhotoParallaxAnimation();
mediaSm.addEventListener('change', handleSectionPhotoParallaxAnimation);

//
// CONTENT
//
// VIDEO
// Variables
let contentVideoPlayVideoFromOffset = 0;
let contentVideoAdjacentSlides = true;
let contentVideoPrevIndex = 0;

const contentVideoEnable = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin})`);
const contentVideo = document.getElementById('contentVideo');
const contentVideoForwards = document.getElementById('contentVideoForwards');
const contentVideoForwardsTimecodes = [
  [0, 0],
  [0, 1.11],
  [1.11, 2.1],
];
const contentVideoBackwards = document.getElementById('contentVideoBackwards');
const contentVideoBackwardsTimecodes = [
  [0.99, 2.1],
  [0, 0.99],
  [0, 0],
];
const contentVideoItems = Array.from(document.querySelectorAll('.js-content-video-item'));

// Methods
const contentVideoHelpers = {
  showVideoForwards: () => {
    contentVideoForwards.classList.remove('is-hidden');
    contentVideoForwards.removeAttribute('aria-hidden');
  },
  hideVideoForwards: () => {
    contentVideoForwards.classList.add('is-hidden');
    contentVideoForwards.setAttribute('aria-hidden', 'true');
  },
  showVideoBackwards: () => {
    contentVideoBackwards.classList.remove('is-hidden');
    contentVideoBackwards.removeAttribute('aria-hidden');
  },
  hideVideoBackwards: () => {
    contentVideoBackwards.classList.add('is-hidden');
    contentVideoBackwards.setAttribute('aria-hidden', 'true');
  },
  toggleVideos: function (isScrollFwd) {
    if (isScrollFwd) {
      this.hideVideoBackwards();
      this.showVideoForwards();
    } else {
      this.hideVideoForwards();
      this.showVideoBackwards();
    }
  },
  gotoSlide: (isScrollFwd, index) => {
    const video = isScrollFwd ? contentVideoForwards : contentVideoBackwards;
    let [, prevEnd] = isScrollFwd
      ? contentVideoForwardsTimecodes[contentVideoPrevIndex]
      : contentVideoBackwardsTimecodes[contentVideoPrevIndex];
    const [start, end] = isScrollFwd ? contentVideoForwardsTimecodes[index] : contentVideoBackwardsTimecodes[index];

    if (contentVideoPrevIndex === index) {
      prevEnd = start;
    }

    // Fire!
    playVideoFromTo(video, prevEnd, end, isScrollFwd);

    contentVideoPrevIndex = index;
  },
};

// Functions
function playVideoFromTo(video, from, to, isScrollFwd) {
  video.pause();
  from = from + contentVideoPlayVideoFromOffset;
  video.currentTime = from;

  // https://stackoverflow.com/a/37172024
  // Preventing race condition between .pause() and .play()
  setTimeout(() => {
    const playPromise = video.play();

    playPromise
      .then(() => {
        contentVideoHelpers.toggleVideos(isScrollFwd);

        const interval = setInterval(() => {
          if (contentVideoAdjacentSlides) {
            contentVideoPlayVideoFromOffset = Math.round((to - video.currentTime + Number.EPSILON) * 100) / 100;
          }

          if (video.currentTime >= to) {
            clearInterval(interval);
            if (contentVideoAdjacentSlides) {
              contentVideoPlayVideoFromOffset = 0;
            }
            video.pause();
          }
        }, 0);
      })
      .catch(() => {});
  }, 100);
}

// Handlers
const handleContentVideoScroll = () => {
  // Detecting item in viewport
  const contentVideoHeight = contentVideo.offsetHeight;
  const contentVideoTop = contentVideo.getBoundingClientRect().top;
  const scrollThreshold = contentVideoTop + contentVideoHeight * 0.5;
  let index = -1;

  for (let i = contentVideoItems.length - 1; i >= 0; i -= 1) {
    const item = contentVideoItems[i];
    const itemTop = item.getBoundingClientRect().top;

    if (itemTop < scrollThreshold) {
      index = i;
      break;
    }
  }

  // Detecting direction
  const isScrollFwd = index > contentVideoPrevIndex;

  // Playing video on scroll
  if (index !== -1 && index !== contentVideoPrevIndex) {
    // Playing video
    contentVideoHelpers.gotoSlide(isScrollFwd, index);
  }
};

const handleContentVideoInit = () => {
  if (contentVideoEnable.matches) {
    window.addEventListener('scroll', handleContentVideoScroll);
  } else {
    window.removeEventListener('scroll', handleContentVideoScroll);
  }
};

// Background toggle
const handleBackgroundToggleScroll = () => {
  const contentVideo = document.getElementById('content-video');

  if (isInViewport(contentVideo, true)) {
    body.classList.add('no-bg-right');
  } else {
    body.classList.remove('no-bg-right');
  }
};

const handleBackgroundToggleInit = () => {
  if (mediaSm.matches) {
    window.removeEventListener('scroll', handleBackgroundToggleScroll);
  } else {
    window.addEventListener('scroll', handleBackgroundToggleScroll);
  }
};

handleBackgroundToggleInit();
mediaSm.addEventListener('change', handleBackgroundToggleInit);

// Init
handleContentVideoInit();
contentVideoEnable.addEventListener('change', handleContentVideoInit);

// IMAGES
let stopContentImagesAnimation = [];

const handleContentImagesAnimation = () => {
  const contentImages = document.querySelectorAll('#content-images > figure');

  if (mediaSm.matches) {
    stopContentImagesAnimation.forEach((stop) => stop());
  } else {
    stopContentImagesAnimation = Array.from(contentImages, (contentImage) => {
      return scrollAnimationProgress(contentImage, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height * 0.75;
        },
      });
    });
  }
};

handleContentImagesAnimation();
mediaSm.addEventListener('change', handleContentImagesAnimation);

//
// TANSKY
//
let stopTanskySpeechAnimation = [];

const handleTanskySpeechAnimation = () => {
  const speechItems = document.querySelectorAll('#tansky-speech > figure, #tansky-speech > div > p');

  if (mediaSm.matches) {
    stopTanskySpeechAnimation.forEach((stop) => stop());
  } else {
    stopTanskySpeechAnimation = Array.from(speechItems, (speechItem) => {
      return scrollAnimationProgress(speechItem, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - (window.innerHeight - 200);
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + (window.innerHeight / 2 - height / 2);
        },
      });
    });
  }
};

handleTanskySpeechAnimation();
mediaSm.addEventListener('change', handleTanskySpeechAnimation);

//
// ENTERPRISE EXPRESS REQUEST FORM
//
const enterpriseExpressRequestForm = document.getElementById('enterprise-express-request-form');

initRequestForm(enterpriseExpressRequestForm);

//
// BACKGROUNDS
//
// Body
const bodyCirclesCommon = {
  width: {
    sm: '450',
    md: '420',
    lg: '938',
  },
  height: {
    sm: '1600',
    md: '1920',
    lg: '3750',
  },
  y: {
    sm: 400,
    md: 960,
    lg: 1875,
  },
  radius: {
    sm: 187,
    md: 250,
    lg: 625,
  },
  blur: {
    sm: '50px',
    md: '75px',
    lg: '125px',
  },
};

const staticUrlString = '/static/promo-static/landing-2021/src/images/pages/premier/index';

const bodyCircles = [
  {
    name: 'left',
    color: 'rgba(0, 194, 255, 0.33)',
    x: {
      sm: 0,
      md: 0,
      lg: 0,
    },
    staticUrl: {
      sm: `${staticUrlString}/bg-sm-left.webp`,
      md: `${staticUrlString}/bg-md-left.jpg`,
      lg: `${staticUrlString}/bg-lg-left.jpg`,
    },
  },
  {
    name: 'right',
    color: 'rgba(63, 58, 255, 0.33)',
    x: {
      sm: 450,
      md: 420,
      lg: 938,
    },
    staticUrl: {
      sm: `${staticUrlString}/bg-sm-right.webp`,
      md: `${staticUrlString}/bg-md-right.jpg`,
      lg: `${staticUrlString}/bg-lg-right.jpg`,
    },
  },
];

const drawBodyCircles = () => {
  const mediaName = mediaSm.matches ? 'sm' : mediaMd.matches ? 'md' : mediaLg.matches ? 'lg' : '';

  for (let bodyCircle of bodyCircles) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('width', bodyCirclesCommon['width'][mediaName]);
    canvas.setAttribute('height', bodyCirclesCommon['height'][mediaName]);

    if (canvas.getContext) {
      const ctx = canvas.getContext('2d');

      ctx.beginPath();
      ctx.arc(bodyCircle.x[mediaName], bodyCirclesCommon.y[mediaName], bodyCirclesCommon.radius[mediaName], 0, 360);
      ctx.filter = `blur(${bodyCirclesCommon.blur[mediaName]})`;
      ctx.fillStyle = bodyCircle.color;
      ctx.fill();

      const el = document.querySelector(`[data-bg=${bodyCircle.name}]`);

      el.style.setProperty('background-image', `url(${JSON.stringify(canvas.toDataURL())})`);
    }
  }
};

const applyStaticBg = () => {
  const mediaName = mediaSm.matches ? 'sm' : mediaMd.matches ? 'md' : mediaLg.matches ? 'lg' : '';

  for (let bodyCircle of bodyCircles) {
    const el = document.querySelector(`[data-bg=${bodyCircle.name}]`);

    el.style.setProperty('background-image', `url(${JSON.stringify(bodyCircle.staticUrl[mediaName])})`);
  }
};

if (isSafari()) {
  applyStaticBg();

  mediaSm.addEventListener('change', applyStaticBg);
  mediaMd.addEventListener('change', applyStaticBg);
  mediaLg.addEventListener('change', applyStaticBg);
} else {
  drawBodyCircles();

  mediaSm.addEventListener('change', drawBodyCircles);
  mediaMd.addEventListener('change', drawBodyCircles);
  mediaLg.addEventListener('change', drawBodyCircles);
}

// Animation
const pageHeight = html.offsetHeight;

html.style.setProperty('--page-height', `${pageHeight}px`);

scrollAnimationProgress(
  html,
  {
    getStart() {
      return 0;
    },
    getEnd(el) {
      const height = el.offsetHeight;
      return height;
    },
    lerpValue: 1,
  },
  eases.linear
);
